import { useState } from 'react'
import { lazy, Suspense } from 'react'
import { MusicGalleryItem } from '@/models/gallery'
import * as stylex from '@stylexjs/stylex'
import { musicStyles as styles } from '@/styles/MusicPage.stylex'

const MusicPopupRaw = lazy(() => import('@/components/popup/MusicPopup'));
const MusicPopup = (props: any) => (
    <Suspense fallback={null}>
        <MusicPopupRaw {...props} />
    </Suspense>
);

/**
 * Sub-component to manage individual hover states for the "Click Me!" indicator.
 * StyleX requires local state for parent-to-child hover effects.
 */
function MusicGalleryItemComponent({
    item,
    onOpen
}: {
    item: MusicGalleryItem,
    onOpen: (item: MusicGalleryItem) => void
}) {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <div
            {...stylex.props(styles.musicGalleryItem)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Music Info Container */}
            <div {...stylex.props(styles.musicInfoContainer)}>
                <div {...stylex.props(styles.musicTitle)}>{item.title}</div>
                <div {...stylex.props(styles.musicStyleTags)}>
                    {item.mode && <span {...stylex.props(styles.modeTag)}>{item.mode}</span>}
                    {item.vocalGender && item.vocalGender !== 'none' && (
                        <span {...stylex.props(styles.vocalTag)}>{item.vocalGender} vocals</span>
                    )}
                    <div
                        {...stylex.props(
                            styles.clickIndicatorMusic,
                            isHovered && styles.indicatorVisible
                        )}
                        onClick={() => onOpen(item)}
                    >
                        Click Me!
                    </div>
                </div>
            </div>

            {/* Audio Player */}
            <div {...stylex.props(styles.audioPlayerContainer)}>
                <audio controls {...stylex.props(styles.audioPlayer)}>
                    <source src={item.audioSrc1} type="audio/mpeg" />
                    <source src={item.audioSrc1} type="audio/wav" />
                    <source src={item.audioSrc1} type="audio/ogg" />
                    Your browser does not support the audio element.
                </audio>
                {item.audioSrc2 && (
                    <audio controls {...stylex.props(styles.audioPlayer)}>
                        <source src={item.audioSrc2} type="audio/mpeg" />
                        <source src={item.audioSrc2} type="audio/wav" />
                        <source src={item.audioSrc2} type="audio/ogg" />
                        Your browser does not support the audio element.
                    </audio>
                )}
            </div>
        </div>
    );
}

interface TextToMusicGalleryProps {
    title: string
    description: string
    modelKey: string
    modelLabel: string
    modelNote: string
    items: MusicGalleryItem[]
}

export function TextToMusicGallery({ title, description, modelKey, modelLabel, modelNote, items }: TextToMusicGalleryProps) {
    const [showPopup, setShowPopup] = useState(false)
    const [selectedItem, setSelectedItem] = useState<MusicGalleryItem>({ id: '', audioSrc1: '', audioSrc2: '', title: '', prompt: '' })

    const openPopup = (item: MusicGalleryItem) => {
        setSelectedItem(item)
        setShowPopup(true)
    }

    const closePopup = () => {
        setShowPopup(false)
    }

    return (
        <>
            <div {...stylex.props(styles.gallerySection)}>
                <h3 {...stylex.props(styles.sectionTitle)}>{title}</h3>
                <p
                    {...stylex.props(styles.galleryDescription)}
                    dangerouslySetInnerHTML={{ __html: description }}
                />
                <div {...stylex.props(styles.musicGalleryList)}>
                    {items.map((item, index) => (
                        <MusicGalleryItemComponent
                            key={index}
                            item={item}
                            onOpen={openPopup}
                        />
                    ))}
                </div>
            </div>

            {/* Music Popup with gallery item data */}
            <MusicPopup
                isOpen={showPopup}
                onClose={closePopup}
                id={selectedItem.id}
                audioSrc1={selectedItem.audioSrc1}
                audioSrc2={selectedItem.audioSrc2}
                modelKey={modelKey}
                modelLabel={modelLabel}
                prompt={selectedItem.prompt}
                style={selectedItem.style}
                mode={selectedItem.mode}
                vocalGender={selectedItem.vocalGender}
                title={selectedItem.title}
                popupTitle="Music Details"
                modelNote={modelNote}
            />
        </>
    )
}